if (!customElements.get('dog-wall-preview')) {
  customElements.define(
    'dog-wall-preview',
    class DogWallPreview extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-wall-dialog]');
        this.desktop = this.querySelector('[data-wall-desktop]');
        this.mobile = this.querySelector('[data-wall-mobile]');
        this.video = this.querySelector('[data-wall-video]');
        this.overlay = this.querySelector('[data-wall-overlay]');
        this.stage = this.querySelector('[data-wall-stage]');
        this.error = this.querySelector('[data-wall-error]');
        this.stream = null;
        this.scale = 1;
        this.originX = 0;
        this.originY = 0;

        this.querySelector('[data-wall-open]')?.addEventListener('click', () => this.open());
        this.querySelector('[data-wall-close]')?.addEventListener('click', () => this.close());
        this.querySelector('[data-wall-copy]')?.addEventListener('click', () => this.copyLink());
        this.querySelector('[data-wall-camera]')?.addEventListener('click', () => this.startCamera());
        this.querySelector('[data-wall-sample]')?.addEventListener('click', () => this.showSample());

        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });

        this.setupOverlayGestures();
        this.preparePainting();

        if (new URLSearchParams(window.location.search).get('view-on-wall') === '1') this.open();
      }

      disconnectedCallback() {
        this.stopCamera();
      }

      isMobile() {
        return window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
      }

      open() {
        if (!this.dialog) return;
        const mobile = this.isMobile();
        if (this.desktop) this.desktop.hidden = mobile;
        if (this.mobile) this.mobile.hidden = !mobile;
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }

      close() {
        this.stopCamera();
        if (typeof this.dialog?.close === 'function') this.dialog.close();
        else this.dialog?.removeAttribute('open');
      }

      async copyLink() {
        const url = this.dataset.url;
        try {
          await navigator.clipboard.writeText(url);
          const button = this.querySelector('[data-wall-copy]');
          if (button) {
            const original = button.textContent;
            button.textContent = button.dataset.copied || 'Link copied';
            setTimeout(() => {
              button.textContent = original;
            }, 1600);
          }
        } catch (error) {
          window.prompt('', url);
        }
      }

      async startCamera() {
        if (!navigator.mediaDevices?.getUserMedia) {
          this.showError('Camera is not available in this browser.');
          return;
        }
        try {
          this.stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { facingMode: { ideal: 'environment' } },
          });
          if (this.video) {
            this.video.srcObject = this.stream;
            await this.video.play();
          }
          if (this.stage) this.stage.hidden = false;
          await this.preparePainting();
          this.showError('');
        } catch (error) {
          this.showError('Allow camera access to preview the painting on your wall.');
        }
      }

      stopCamera() {
        this.stream?.getTracks().forEach((track) => track.stop());
        this.stream = null;
        if (this.video) this.video.srcObject = null;
      }

      showSample() {
        if (this.stage) this.stage.hidden = false;
        if (this.desktop) this.desktop.hidden = true;
        if (this.mobile) this.mobile.hidden = false;
      }

      showError(message) {
        if (!this.error) return;
        this.error.hidden = !message;
        this.error.textContent = message;
      }

      async preparePainting() {
        if (!this.overlay || this.overlay.dataset.processed === 'true') return;
        const src = this.dataset.image || this.overlay.getAttribute('src');
        if (!src) return;
        try {
          const isolated = await this.isolatePainting(src);
          if (isolated) {
            this.overlay.src = isolated;
            this.overlay.dataset.processed = 'true';
          }
        } catch (error) {
          this.overlay.style.mixBlendMode = 'multiply';
        }
      }

      isolatePainting(src) {
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(image, 0, 0);
            const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = pixels.data;
            for (let i = 0; i < data.length; i += 4) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];
              const max = Math.max(r, g, b);
              const min = Math.min(r, g, b);
              const sat = max === 0 ? 0 : (max - min) / max;
              const lum = (r + g + b) / 3;
              if (lum > 208 && sat < 0.22) data[i + 3] = 0;
              else if (lum > 188 && sat < 0.14) data[i + 3] = 0;
            }
            ctx.putImageData(pixels, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          image.onerror = reject;
          image.src = src;
        });
      }

      setupOverlayGestures() {
        if (!this.overlay) return;
        let lastDistance = 0;
        let dragging = false;
        let startX = 0;
        let startY = 0;

        const apply = () => {
          this.overlay.style.transform = `translate(${this.originX}px, ${this.originY}px) scale(${this.scale})`;
        };

        this.overlay.addEventListener('pointerdown', (event) => {
          dragging = true;
          startX = event.clientX - this.originX;
          startY = event.clientY - this.originY;
          this.overlay.setPointerCapture(event.pointerId);
        });
        this.overlay.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          this.originX = event.clientX - startX;
          this.originY = event.clientY - startY;
          apply();
        });
        this.overlay.addEventListener('pointerup', () => {
          dragging = false;
        });
        this.overlay.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length === 2) {
              lastDistance = Math.hypot(
                event.touches[0].clientX - event.touches[1].clientX,
                event.touches[0].clientY - event.touches[1].clientY
              );
            }
          },
          { passive: true }
        );
        this.overlay.addEventListener(
          'touchmove',
          (event) => {
            if (event.touches.length === 2) {
              const distance = Math.hypot(
                event.touches[0].clientX - event.touches[1].clientX,
                event.touches[0].clientY - event.touches[1].clientY
              );
              if (lastDistance) {
                this.scale = Math.min(3, Math.max(0.4, this.scale * (distance / lastDistance)));
                apply();
              }
              lastDistance = distance;
            }
          },
          { passive: true }
        );
      }
    }
  );
}
